import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Lock, AlertCircle } from "lucide-react";
import { API } from "@/App";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");

  useEffect(() => {
    const auth = localStorage.getItem("admin_auth");
    if (auth) {
      navigate("/admin/dashboard");
    }
  }, [navigate]);

  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // État pour le compte à rebours (en secondes)
  const [retryAfter, setRetryAfter] = useState(0);

  useEffect(() => {
    // Si le compteur est à 0, on ne fait rien
    if (retryAfter <= 0) return;

    // On crée un intervalle qui retire 1 seconde toutes les 1000ms
    const timer = setInterval(() => {
      setRetryAfter((prev) => prev - 1);
    }, 1000);

    // Nettoyage de l'intervalle si le composant est fermé
    return () => clearInterval(timer);
  }, [retryAfter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username,
          mot_de_passe: password,
        }),
      });

      const data = await response.json();
      console.log("Login response:", data);
      console.log("Response status:", response.ok);

      if (response.ok) {
        // Clear user session if login as admin
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("user_type");
        localStorage.removeItem("repreneur_id");
        localStorage.removeItem("repreneur_result");
        localStorage.removeItem("cedant_result");
        localStorage.removeItem("pending_user");
        localStorage.removeItem("pending_interests");
        sessionStorage.clear();

        localStorage.setItem("admin_token", data.access_token);
        localStorage.setItem("admin_info", JSON.stringify(data.admin));
        console.log("Stored token, navigating to dashboard");
        navigate("/admin/dashboard");
      } else {
        if (response.status === 429) {
          // 1. On récupère le temps d'attente suggéré par le serveur
          // Si le header n'est pas là, on met 60 secondes par défaut
          const waitTime = parseInt(response.headers.get("Retry-After")) || 60;

          // 2. On lance le blocage UI
          setRetryAfter(waitTime);
          setError(`Trop de tentatives. Veuillez patienter ${waitTime} secondes.`);
        } else {
          // Erreur classique (401, etc.)
          setError(data.detail || "Identifiants incorrects");
        }
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("Erreur de connexion au serveur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-background flex flex-col items-center justify-center p-8"
      data-testid="admin-login"
    >
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between mb-12">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-foreground/40 hover:text-primary transition-colors font-semibold uppercase tracking-widest text-xs"
            data-testid="back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </button>
          <span className="text-2xl font-bold text-primary">Sequenti</span>
        </div>

        <Card className="p-12 border-none bg-white shadow-xl rounded-lg border border-border/40 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-2 bg-accent" />
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/5 rounded-full mb-6">
              <Lock className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-4xl font-black text-primary mb-3">
              Administration
            </h1>
            <p className="text-lg text-foreground/40 font-medium">
              Accédez à votre tableau de bord sécurisé
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {error && (
              <div className="flex items-center gap-3 p-4 bg-destructive/5 text-destructive rounded-lg border border-destructive/10 text-base font-semibold">
                <AlertCircle className="w-5 h-5" />
                {error}
              </div>
            )}

            <div className="space-y-3">
              <Label
                htmlFor="username"
                className="text-base font-bold text-primary"
              >
                Identifiant
              </Label>
              <Input
                id="username"
                type="text"
                className="h-14 text-lg rounded-lg border-border/50"
                disabled={retryAfter > 0}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Votre identifiant"
                required
                data-testid="input-username"
              />
            </div>

            <div className="space-y-3">
              <Label
                htmlFor="password"
                className="text-base font-bold text-primary"
              >
                Mot de passe
              </Label>
              <Input
                id="password"
                type="password"
                className="h-14 text-lg rounded-lg border-border/50"
                disabled={retryAfter > 0}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="input-password"
              />
            </div>

            <div className="flex justify-end">
              <Link
                to="/admin/forgot-password"
                className="text-sm font-bold text-primary hover:text-accent transition-colors"
              >
                Mot de passe oublié ?
              </Link>
            </div>

            <Button
              type="submit"
              disabled={loading || retryAfter > 0}
              className={`w-full text-xl h-16 rounded-lg shadow-lg transition-all active:scale-95 mt-4 ${retryAfter > 0 ? "bg-destructive/60 cursor-not-allowed" : "bg-primary text-white"
                }`}
              data-testid="login-button"
            >
              {loading
                ? "Connexion en cours..."
                : retryAfter > 0
                  ? `Réessayer dans ${retryAfter}s`
                  : "Se connecter"}
            </Button>
          </form>

        </Card>
      </div>
    </div>
  );
}
